/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable max-lines */
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { deleteFilter, makeDefaultFilter } from 'api/Filter';

import { ia, iaRa } from 'lib/helpers/utility';
import { useFilters } from 'lib/hooks/queries/filters/useFilters';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';
import useModal from 'lib/hooks/useModal';
import { useOperatingSystem } from 'lib/hooks/useOperatingSystem';

import { applyFilterHandler } from 'components/shared/Filter/lib/helpers/handleAppliedFilter';
import SaveFilterModal from 'components/shared/Filters/components/SaveFilterModal';
import Key from 'components/shared/Key/Key';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import ToggleSwitch from 'components/shared/ToggleSwitch/ToggleSwitch';

import { checkActiveItem } from '../lib/checkActiveItem';

const TooltipContent = ({ label, index, os }) => {
  const isMac = os === 'mac';

  if (isMac) {
    return (
      <div className="flex items-center gap-1">
        <span>{label}</span> <Key icon="chevron-up-2-linear" /> <span>+</span>
        <Key icon="keys-option" />
        <span>+</span> <Key text={`${index + 1}`} />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <span>{label}</span> <Key text="ctrl" /> <span>+</span> <Key text="alt" />
      <span>+</span> <Key text={`${index + 1}`} />
    </div>
  );
};

const ViewTabs = ({
  items = [],
  category,
  filters,
  title,
  setFilters,
  defaultFilters,
  setInitialFilters,
  setIsEditingFilter,
  removeDefaults = false
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status');
  const filter = searchParams.get('view');
  const os = useOperatingSystem();

  const navigate = useNavigate();
  const location = useLocation();

  const { isOpen, closeModal, openModal } = useModal();
  const { isOpen: isEditOpen, closeModal: closeEditModal, openModal: openEditModal } = useModal();

  const activeItem = useMemo(
    () => checkActiveItem(items, status, location, filter),
    [items, status, location, filter]
  );

  const onSwitchItemClick = (item) => {
    if (item.navigate) {
      navigate(item.navigate);
      return;
    }
    setIsEditingFilter(false);
    setSearchParams((prev) => {
      prev.delete('view');
      if (item?.value === null) {
        prev.delete('status');
        setFilters({});
        setInitialFilters({});
      } else {
        prev.set('status', item?.value);
        setFilters({});
        setInitialFilters({});
      }
      return prev;
    });
  };

  const { data: filtersData } = useFilters({
    params: { category },
    dependencies: [category]
  });

  useEffect(() => {
    if (filtersData?.default?.filters && !status) {
      setSearchParams((prev) => {
        if (!filtersData?.default?.filters) return prev;
        prev.set('view', filtersData?.default?.name);
        return prev;
      });
      setFilters(applyFilterHandler(defaultFilters, filtersData?.default));
    }
  }, [filtersData?.default]);

  const onFilterClick = (filter, isEditing = false) => {
    setIsEditingFilter(isEditing);
    setSearchParams((prev) => {
      prev.delete('status');
      if (filter.name === null) {
        prev.delete('view');
        setFilters({});
        setInitialFilters({});

        return prev;
      }
      const selectedFilter = iaRa(filtersData?.filters).find((f) => f.name === filter.name);

      if (!selectedFilter) return prev;

      prev.set('view', filter.name);
      const appliedFilters = applyFilterHandler(defaultFilters, selectedFilter);
      setFilters(appliedFilters);
      setInitialFilters(appliedFilters);
      return prev;
    });
  };

  const { mutate: deleteFilterHandler, isLoading: isDeletingFilter } = useCustomMutation({
    mutationFn: (params) => deleteFilter(navigate, params),
    successMessage: 'Filter deleted successfully',
    errorMessage: 'Failed to delete filter. Please try again later.',
    invalidateQueryKey: ['default-filters', ['filters', category]],
    onSuccess: () => {
      setFilters({});
      setInitialFilters({});
      setSearchParams((prev) => {
        const view = prev.get('view');
        if (view === isOpen.name) {
          prev.delete('view');
        }
        return prev;
      });
      closeModal();
    }
  });
  const { mutate: makeDefaultFilterHandler, isLoading: isMakingDefaultFilter } = useCustomMutation({
    mutationFn: (params) => makeDefaultFilter(navigate, params),
    successMessage: 'Filter set as default successfully',
    errorMessage: 'Failed to set filter as default. Please try again later.',
    invalidateQueryKey: ['default-filters', ['filters', category]]
  });

  const handleDeleteFilter = () => deleteFilterHandler({ id: isOpen.id });

  const hasNavigate = iaRa(items).some((item) => !!item.navigate);

  const filtered = [
    ...(!hasNavigate
      ? [
          {
            label: `All ${title || category}`,
            value: null
          }
        ]
      : []),
    ...items
  ];
  const removeItems = removeDefaults && !ia(filtersData?.filters) ? [] : filtered;

  const toggleItems = useMemo(() => {
    let firstActionAdded = false;
    return iaRa(removeItems)
      .concat(
        iaRa(filtersData?.filters).map((filter) => {
          const item = {
            label: filter.name,
            value: filter.name,
            default: filter.is_default,
            onClick: () => onFilterClick(filter, true),
            className: 'px-2 hover:bg-primary-50',
            actions: [
              {
                label: 'Rename View',
                value: 'edit',
                icon: 'editor-firstline3rec-mono',
                onClick: () => openEditModal(filter),
                textClassName: 'text-neutral-900'
              },
              {
                label: filter.is_default ? 'Unset as default view' : 'Set as default view',
                value: 'edit',
                icon: 'star-1-mono',
                onClick: () => {
                  if (isMakingDefaultFilter) return;
                  makeDefaultFilterHandler({
                    id: filter.id,
                    category,
                    isDefault: !filter.is_default
                  });
                },
                textClassName: 'text-neutral-900'
              },
              {
                label: 'Delete',
                value: 'delete',
                icon: 'trash-mono',
                onClick: () => openModal({ id: filter.id, name: filter.name }),
                textClassName: 'text-neutral-900'
              }
            ]
          };
          if (!firstActionAdded && item.actions) {
            item.split = true;
            firstActionAdded = true;
          }
          return item;
        })
      )
      .map((item, index) => ({
        ...item,
        tooltip: <TooltipContent label={item.label} index={index} os={os} />
      }));
  }, [filtersData, items, category]);

  useEffect(() => {
    let buffer = '';
    let timeout;

    const applyChange = () => {
      const index = parseInt(buffer, 10) - 1;
      if (!isNaN(index) && index >= 0 && index < toggleItems.length) {
        const selectedItem = toggleItems[index];
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          const isFromItems = index < iaRa(items).length;

          if (selectedItem?.value === null) {
            newParams.delete('view');
            newParams.delete('status');
            setFilters({});
            setInitialFilters({});
          } else {
            newParams.set(isFromItems ? 'status' : 'view', selectedItem?.value);
            newParams.delete(isFromItems ? 'view' : 'status');
            setFilters({});
            setInitialFilters({});
          }

          return newParams;
        });
      }

      buffer = '';
    };
    const handleKeyDown = (e) => {
      if (/^Digit\d$/.test(e.code) && e.altKey) {
        e.preventDefault();
        clearTimeout(timeout);
        buffer += e.code.slice(-1);

        timeout = setTimeout(() => {
          applyChange();
        }, 0);
      }
    };

    const handleKeyUp = (e) => {
      if (!/^Digit\d$/.test(e.code) || !e.altKey) {
        buffer = '';
        clearTimeout(timeout);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      clearTimeout(timeout);
    };
  }, [setSearchParams, toggleItems, items]);

  return (
    <>
      <div className="flex w-full gap-2">
        <ToggleSwitch
          items={toggleItems}
          onClick={onSwitchItemClick}
          active={activeItem?.value || filter || null}
        />
      </div>

      {isOpen && (
        <Confirm
          isOpen={isOpen}
          variant="danger"
          loading={isDeletingFilter}
          handleClose={closeModal}
          handleContinue={handleDeleteFilter}
          title={`Delete view "${isOpen.name}"`}
          icon="trash-mono"
          message="You cannot do undo this action"
          confirmText="Delete"
          cancelText="Cancel"
        />
      )}
      {isEditOpen && (
        <SaveFilterModal
          title="Rename View"
          isOpen={isEditOpen}
          currentView={isEditOpen}
          onCancel={closeEditModal}
          filters={filters}
          category={category}
          currentViews={filtersData?.filters}
        />
      )}
    </>
  );
};

export default ViewTabs;
